import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Table, Tooltip } from 'antd';
import type { ColumnsType, TablePaginationConfig } from 'antd/es/table';
import type { SorterResult } from 'antd/es/table/interface';
import { CheckOutlined } from '@ant-design/icons';
import { useGetStudiesQuery, type StudyRow } from 'services/exploreService';
import { useUrlFilters } from 'hooks/useUrlFilters';
import FilterBar from 'components/explore/FilterBar';
import { Empty, Meter, PageHead, fmt, studyHref } from 'components/explore';

const PAGE_SIZE = 50;

const Studies: React.FC = () => {
  const navigate = useNavigate();
  const { filters, page, update, clear, activeCount } = useUrlFilters(PAGE_SIZE);
  const { data, isFetching, error } = useGetStudiesQuery(filters);

  const columns: ColumnsType<StudyRow> = [
    {
      title: 'Study',
      dataIndex: 'icid',
      key: 'icid',
      sorter: true,
      sortOrder: filters.sort === 'icid' ? (filters.order === 'asc' ? 'ascend' : 'descend') : null,
      render: (v, r) => (
        <span>
          <span className="ax-mono">{v ?? r.uuid.slice(0, 8)}</span>
          {r.foreign_id && (
            <div className="ax-faint" style={{ fontSize: 11.5 }}>
              {r.foreign_id}
            </div>
          )}
        </span>
      ),
    },
    {
      title: 'Source',
      dataIndex: 'datasource',
      key: 'datasource',
      render: v => v ?? <span className="ax-faint">–</span>,
    },
    {
      title: 'Year',
      dataIndex: 'visit_year',
      key: 'visit_year',
      sorter: true,
      sortOrder:
        filters.sort === 'visit_year' ? (filters.order === 'asc' ? 'ascend' : 'descend') : null,
      className: 'num',
    },
    {
      title: 'Patient',
      key: 'patient',
      render: (_, r) =>
        [r.age_at_visit && `${r.age_at_visit}y`, r.gender].filter(Boolean).join(' · ') || '–',
    },
    {
      title: 'DICOMs',
      key: 'dicoms',
      sorter: true,
      sortOrder:
        filters.sort === 'dicoms' ? (filters.order === 'asc' ? 'ascend' : 'descend') : null,
      align: 'right',
      className: 'num',
      render: (_, r) => fmt(r.stats.dicoms || r.number_of_dicoms),
    },
    {
      title: 'Scanners',
      key: 'scanners',
      render: (_, r) => (
        <span className="ax-muted" style={{ fontSize: 12.5 }}>
          {r.stats.manufacturers.join(', ') || '–'}
        </span>
      ),
    },
    {
      title: (
        <Tooltip title="DICOMs in the study with an AI view prediction, a tech view label, segmentation, keypoints">
          <span style={{ display: 'inline-flex', flexDirection: 'column', gap: 2 }}>
            <span>Coverage</span>
            <span className="ax-legend" style={{ fontSize: 11, fontWeight: 400, gap: 8 }}>
              <span>
                <i style={{ ['--c' as any]: 'var(--s1)' }} />
                AI view
              </span>
              <span>
                <i style={{ ['--c' as any]: 'var(--s2)' }} />
                Tech view
              </span>
              <span>
                <i style={{ ['--c' as any]: 'var(--s3)' }} />
                Seg
              </span>
              <span>
                <i style={{ ['--c' as any]: 'var(--s4)' }} />
                KP
              </span>
            </span>
          </span>
        </Tooltip>
      ),
      key: 'coverage',
      render: (_, r) => (
        <span style={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '2px 10px' }}>
          <Meter
            n={r.stats.view_prediction}
            total={r.stats.dicoms}
            color="var(--s1)"
            title="AI view prediction"
          />
          <Meter
            n={r.stats.view_label}
            total={r.stats.dicoms}
            color="var(--s2)"
            title="Tech view label"
          />
          <Meter
            n={r.stats.segmentation}
            total={r.stats.dicoms}
            color="var(--s3)"
            title="Segmentation"
          />
          <Meter n={r.stats.keypoints} total={r.stats.dicoms} color="var(--s4)" title="Keypoints" />
        </span>
      ),
    },
    {
      title: 'Report',
      dataIndex: 'has_report',
      key: 'has_report',
      align: 'center',
      render: v =>
        v ? (
          <CheckOutlined aria-label="Has report" style={{ color: 'var(--good)' }} />
        ) : (
          <span className="ax-faint">–</span>
        ),
    },
    {
      title: 'Added',
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: true,
      sortOrder:
        !filters.sort || filters.sort === 'created_at'
          ? filters.order === 'asc'
            ? 'ascend'
            : 'descend'
          : null,
      className: 'num',
      render: v => (v ? new Date(v).toLocaleDateString() : '–'),
    },
  ];

  const onChange = (
    p: TablePaginationConfig,
    _f: unknown,
    s: SorterResult<StudyRow> | SorterResult<StudyRow>[]
  ) => {
    const sorter = Array.isArray(s) ? s[0] : s;
    const key = (sorter?.columnKey as string) || undefined;
    const sortChanged =
      key !== (filters.sort ?? 'created_at') ||
      (sorter?.order === 'ascend' ? 'asc' : 'desc') !== (filters.order ?? 'desc');
    if (sorter?.order && sortChanged) {
      update({ sort: key, order: sorter.order === 'ascend' ? 'asc' : 'desc', page: 1 });
    } else if (!sorter?.order && filters.sort) {
      update({ sort: undefined, order: undefined, page: 1 });
    } else {
      update({ page: p.current });
    }
  };

  return (
    <>
      <FilterBar
        filters={filters}
        update={update}
        clear={clear}
        activeCount={activeCount}
        total={data?.total}
        unit="studies"
        level="study"
        placeholder="Study ICID, DICOM ICID or foreign id"
      />
      <div className="ax-page">
        <PageHead
          title="Studies"
          lede="Click a study to see every DICOM in it, grouped by view. Coverage counts DICOMs in the study with each kind of annotation."
        />
        {error ? (
          <Alert
            type="error"
            showIcon
            message="Could not load studies"
            description="The Explorer API returned an error."
          />
        ) : (
          <div className="ax-panel" style={{ padding: 0 }}>
            <Table<StudyRow>
              className="ax-table"
              rowKey="uuid"
              size="middle"
              loading={isFetching}
              columns={columns}
              dataSource={data?.items ?? []}
              scroll={{ x: 960 }}
              locale={{
                emptyText: (
                  <Empty title="No studies match these filters">Try removing a filter.</Empty>
                ),
              }}
              onChange={onChange}
              onRow={r => ({
                onClick: () => navigate(studyHref(r.icid ?? r.uuid)),
                onKeyDown: e => {
                  if (e.key === 'Enter') navigate(studyHref(r.icid ?? r.uuid));
                },
                tabIndex: 0,
              })}
              pagination={{
                current: page,
                pageSize: PAGE_SIZE,
                total: data?.total ?? 0,
                showSizeChanger: false,
                showTotal: (total, range) => `${fmt(range[0])}–${fmt(range[1])} of ${fmt(total)}`,
              }}
            />
          </div>
        )}
      </div>
    </>
  );
};

export default Studies;
